import {
  Stack,
  Typography,
  IconButton,
  Box,
  Paper,
  Avatar,
  Chip,
  CircularProgress,
  LinearProgress,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  Divider,
  TablePagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  PersonOutline as PersonOutlineIcon,
  CalendarToday as CalendarTodayIcon,
  Badge as BadgeIcon,
  Wc as GenderIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSessionContext } from "@/contexts/SessionContext";
import { SecondaryButton } from "@/components/common/SecondaryButton";
import { useState, useEffect, useMemo } from "react";
import { currentFeatures } from "@/configs/Settings";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import { useFormatter } from "@/utils/useFormatter";
import { ContentType } from "./TypeSelector";
import dayjs from "dayjs";
import { normalizeSearchText } from "../utils/postAttendanceHelpers";

interface Patient {
  id: string;
  name: string;
  phone?: string;
  cpf?: string;
  birthDate?: string;
  gender?: string;
}

interface PatientSelectorProps {
  patients: Patient[];
  loading: boolean;
  selectedPatient: string;
  contentType: ContentType;
  onPatientChange: (patientId: string) => void;
  onSearch: () => void;
  onBack: () => void;
  hasSearched?: boolean; // Prop para controlar se já foi feita uma busca
}

/**
 * Componente melhorado para seleção de paciente
 * Apresenta visual mais limpo e focado após seleção do tipo de conteúdo
 */
export function PatientSelector({
  patients,
  loading,
  selectedPatient,
  contentType,
  onPatientChange,
  onSearch,
  onBack,
  hasSearched: externalHasSearched = false,
}: PatientSelectorProps) {
  const { access } = useSessionContext();
  const navigate = useNavigate();
  const [hasSearched, setHasSearched] = useState(externalHasSearched);
  const [searchQuery, setSearchQuery] = useState("");

  // Estados para paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Estado para armazenar dados dos tutores mapeados por ID do paciente
  const [tutorsData, setTutorsData] = useState<
    Record<string, { name: string; phone: string }>
  >({});
  const [loadingTutors, setLoadingTutors] = useState(false);

  // Sincroniza o estado interno com a prop externa
  useEffect(() => {
    setHasSearched(externalHasSearched);
  }, [externalHasSearched]);

  // Reseta a página quando a busca mudar
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  // Busca automática quando um paciente é selecionado
  useEffect(() => {
    if (selectedPatient && !hasSearched && !loading) {
      // Pequeno delay para melhorar a experiência do usuário
      const timer = setTimeout(() => {
        if (selectedPatient && !hasSearched) {
          onSearch();
          setHasSearched(true);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient]);

  // Manipula o clique em um paciente da lista
  const handlePatientClick = (patientId: string) => {
    if (loading) return; // Previne cliques durante carregamento
    onPatientChange(patientId);
  };

  // Busca tutores para os pacientes quando não for defaultApp (modo vet)
  useEffect(() => {
    if (currentFeatures.defaultApp || !patients?.length || loading) {
      return;
    }

    const patientIds = patients
      .map((patient) => patient.id)
      .filter((id) => id && !tutorsData[id]);

    if (patientIds.length === 0) {
      return;
    }

    const fetchTutors = async () => {
      setLoadingTutors(true);
      try {
        const patientIdsParam = patientIds.join(",");

        const response = await GetRelatedPerson({
          patient: patientIdsParam,
          vetRelated: true,
        });

        if (response?.data?.entry) {
          const tutorsMap: Record<string, { name: string; phone: string }> = {};

          response.data.entry.forEach((entry: any) => {
            const relatedPerson = entry.resource;
            if (relatedPerson?.patient?.reference) {
              const patientRef = relatedPerson.patient.reference;
              const patientId = patientRef.replace("Patient/", "");

              const tutorNameObj = relatedPerson.name?.find(
                (n: any) => n.use === "official" || n.use === "usual",
              );
              const tutorName = tutorNameObj?.text || "-";

              const tutorPhoneObj = relatedPerson.telecom?.find(
                (t: any) => t.system === "phone",
              );
              const tutorPhone = tutorPhoneObj?.value || "";

              if (patientId) {
                tutorsMap[patientId] = {
                  name: useFormatter.formatNameDisplay(tutorName) || "-",
                  phone: tutorPhone,
                };
              }
            }
          });

          setTutorsData((prev) => ({
            ...prev,
            ...tutorsMap,
          }));
        }
      } catch (error) {
        console.error("Erro ao buscar tutores:", error);
      } finally {
        setLoadingTutors(false);
      }
    };

    fetchTutors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patients?.length, loading]);

  // Filtra pacientes baseado na busca (nome normalizado sem acentos para comparação confiável)
  const filteredPatients = useMemo(() => {
    if (!searchQuery.trim()) return patients;

    const query = normalizeSearchText(searchQuery.trim());
    const queryDigits = searchQuery.replace(/[^0-9]/g, "");

    return patients.filter((patient) => {
      const nameMatch = normalizeSearchText(patient.name || "").includes(query);
      const cpfMatch =
        queryDigits.length > 0 &&
        patient.cpf?.replace(/[^0-9]/g, "").includes(queryDigits);
      const tutorData = tutorsData[patient.id];
      const tutorMatch = tutorData?.name
        ? normalizeSearchText(tutorData.name).includes(query)
        : false;
      const phoneMatch =
        (queryDigits.length > 0 &&
          (tutorData?.phone?.replace(/[^0-9]/g, "").includes(queryDigits) ||
            patient.phone?.replace(/[^0-9]/g, "").includes(queryDigits))) ||
        false;

      return nameMatch || cpfMatch || tutorMatch || phoneMatch;
    });
  }, [patients, searchQuery, tutorsData]);

  // Calcula pacientes paginados
  const paginatedPatients = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredPatients.slice(startIndex, endIndex);
  }, [filteredPatients, page, rowsPerPage]);

  // Manipula mudança de página
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Manipula mudança de itens por página
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reseta para primeira página
  };

  // Formata CPF
  const formatCPF = (cpf?: string) => {
    if (!cpf) return "-";
    const cleaned = cpf.replace(/[^0-9]/g, "");
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    return cpf;
  };

  // Formata data de nascimento
  const formatBirthDate = (birthDate?: string) => {
    if (!birthDate) return "-";
    return dayjs(birthDate).format("DD/MM/YYYY");
  };

  // Calcula idade
  const calculateAge = (birthDate?: string) => {
    if (!birthDate) return null;
    const age = dayjs().diff(dayjs(birthDate), "year");
    return age > 0 ? `${age} anos` : null;
  };

  // Formata gênero
  const formatGender = (gender?: string) => {
    if (!gender) return "-";
    const genderMap: Record<string, string> = {
      male: "Masculino",
      female: "Feminino",
      other: "Outro",
      unknown: "Não informado",
    };
    return genderMap[gender] || gender;
  };

  // Texto do tipo de conteúdo selecionado
  const contentTypeLabel =
    contentType === "medications" ? "Medicamentos" : "Exames";

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      {/* Header com breadcrumb visual */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: "background.paper",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton
              onClick={onBack}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={contentTypeLabel}
                  size="small"
                  color={
                    contentType === "medications" ? "primary" : "secondary"
                  }
                  sx={{ fontWeight: 600 }}
                />
                <Typography variant="body2" color="text.secondary">
                  →
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  Selecione o Paciente
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Escolha um paciente para visualizar seus{" "}
                {contentTypeLabel.toLowerCase()}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {/* Área de busca e lista */}
      <Stack spacing={2}>
        {/* Campo de filtro */}
        <TextField
          fullWidth
          variant="filled"
          placeholder="Filtre pacientes por nome ou pelo CPF"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery("")}
                  sx={{ mr: -1 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    ✕
                  </Typography>
                </IconButton>
              </InputAdornment>
            ),
          }}
          disabled={loading}
        />

        {/* Indicador de carregamento de tutores */}
        {loadingTutors && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: "info.lighter",
              borderRadius: 1,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Carregando informações dos tutores...
              </Typography>
            </Stack>
          </Paper>
        )}

        {/* Barra de progresso durante busca automática */}
        {loading && selectedPatient && !hasSearched && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: "primary.lighter",
              borderRadius: 2,
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500} gutterBottom>
                    Buscando {contentTypeLabel.toLowerCase()} do paciente...
                  </Typography>
                  <LinearProgress
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "primary.light",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Aguarde enquanto carregamos os dados do paciente selecionado
              </Typography>
            </Stack>
          </Paper>
        )}

        {/* Lista de pacientes */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {filteredPatients.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <PersonOutlineIcon
                sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
              />
              <Typography variant="body1" color="text.secondary">
                {searchQuery
                  ? "Nenhum paciente encontrado"
                  : "Nenhum paciente disponível"}
              </Typography>
              {searchQuery && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Tente buscar com outros termos
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <List disablePadding>
                {paginatedPatients.map((patient, index) => {
                  const tutorData = tutorsData[patient.id];
                  const isSelected = selectedPatient === patient.id;
                  const age = calculateAge(patient.birthDate);

                  return (
                    <Box key={patient.id}>
                      <ListItem
                        disablePadding
                        sx={{
                          backgroundColor: isSelected
                            ? "primary.lighter"
                            : "transparent",
                          "&:hover": "action.hover",
                          transition: "background-color 0.2s",
                        }}
                      >
                        <ListItemButton
                          onClick={() => handlePatientClick(patient.id)}
                          disabled={loading}
                          sx={{
                            py: 2,
                            px: 2,
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor: isSelected
                                  ? "primary.main"
                                  : "primary.light",
                                width: 48,
                                height: 48,
                              }}
                            >
                              <PersonOutlineIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography
                                  variant="body1"
                                  fontWeight={isSelected ? 600 : 500}
                                >
                                  {patient.name}
                                </Typography>
                                {isSelected && loading && (
                                  <CircularProgress size={16} />
                                )}
                              </Stack>
                            }
                            secondary={
                              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                {/* Informações do paciente */}
                                <Stack
                                  direction="row"
                                  spacing={1.5}
                                  alignItems="center"
                                  flexWrap="wrap"
                                >
                                  {currentFeatures.defaultApp &&
                                    patient.cpf && (
                                      <>
                                        <Stack
                                          direction="row"
                                          spacing={0.5}
                                          alignItems="center"
                                        >
                                          <BadgeIcon
                                            sx={{
                                              fontSize: 14,
                                              color: "text.secondary",
                                            }}
                                          />
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            {formatCPF(patient.cpf)}
                                          </Typography>
                                        </Stack>
                                      </>
                                    )}
                                  {patient.birthDate && (
                                    <>
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                      >
                                        <CalendarTodayIcon
                                          sx={{
                                            fontSize: 14,
                                            color: "text.secondary",
                                          }}
                                        />
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {formatBirthDate(patient.birthDate)}
                                          {age && ` (${age})`}
                                        </Typography>
                                      </Stack>
                                    </>
                                  )}
                                  {patient.gender && (
                                    <>
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                      >
                                        <GenderIcon
                                          sx={{
                                            fontSize: 14,
                                            color: "text.secondary",
                                          }}
                                        />
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {formatGender(patient.gender)}
                                        </Typography>
                                      </Stack>
                                    </>
                                  )}
                                  {patient.phone && (
                                    <>
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                      >
                                        <PhoneIcon
                                          sx={{
                                            fontSize: 14,
                                            color: "text.secondary",
                                          }}
                                        />
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {patient.phone}
                                        </Typography>
                                      </Stack>
                                    </>
                                  )}
                                </Stack>

                                {/* Informações do tutor (modo vet) */}
                                {!currentFeatures.defaultApp && tutorData && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    sx={{ mt: 0.5 }}
                                  >
                                    <PersonIcon
                                      sx={{
                                        fontSize: 14,
                                        color: "text.secondary",
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Tutor: {tutorData.name}
                                    </Typography>
                                    {tutorData.phone && (
                                      <>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          •
                                        </Typography>
                                        <PhoneIcon
                                          sx={{
                                            fontSize: 14,
                                            color: "text.secondary",
                                          }}
                                        />
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          noWrap
                                        >
                                          {tutorData.phone}
                                        </Typography>
                                      </>
                                    )}
                                  </Stack>
                                )}
                              </Stack>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                      {index < paginatedPatients.length - 1 && <Divider />}
                    </Box>
                  );
                })}
              </List>

              {/* Paginação */}
              <TablePagination
                component="div"
                count={filteredPatients.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Itens por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
                sx={{
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              />
            </>
          )}
        </Paper>

        {/* Informações e ações */}
        {access?.roles?.includes("practitioner") && selectedPatient && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <SecondaryButton
              width="auto"
              height="40px"
              onClick={() => {
                if (selectedPatient) {
                  navigate("/summary", {
                    state: {
                      patientId: selectedPatient
                        ?.split("-")?.[1]
                        ?.replace(/[^a-zA-Z0-9]/g, ""),
                    },
                  });
                }
              }}
            >
              Acessar registro completo
            </SecondaryButton>
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
